import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, Briefcase, DollarSign, X, ChevronDown, Check } from 'lucide-react';
import { useProvinces, useDistricts } from '@/hooks/useAddress';
import { cn } from '@/lib/utils';
import type { CandidateFilter } from '@/types/candidates/candidates.types';

const SALARY_RANGES = [
  { label: 'Tất cả', min: null, max: null },
  { label: 'Dưới 10 triệu', min: 0, max: 10000000 },
  { label: '10 - 15 triệu', min: 10000000, max: 15000000 },
  { label: '15 - 20 triệu', min: 15000000, max: 20000000 },
  { label: '20 - 25 triệu', min: 20000000, max: 25000000 },
  { label: '25 - 30 triệu', min: 25000000, max: 30000000 },
  { label: '30 - 50 triệu', min: 30000000, max: 50000000 },
  { label: 'Trên 50 triệu', min: 50000000, max: null },
];

const EXPERIENCE_RANGES = [
  { label: 'Tất cả', min: null, max: null },
  { label: 'Chưa có kinh nghiệm', min: 0, max: 0 },
  { label: 'Dưới 1 năm', min: 0, max: 1 },
  { label: '1 - 2 năm', min: 1, max: 2 },
  { label: '2 - 3 năm', min: 2, max: 3 },
  { label: '3 - 5 năm', min: 3, max: 5 },
  { label: 'Trên 5 năm', min: 5, max: null },
];

interface CandidateAdvancedFilterProps {
  onFilter: (filters: Partial<CandidateFilter>) => void;
  initialFilters?: Partial<CandidateFilter>;
  className?: string;
}

export function CandidateAdvancedFilter({ onFilter, initialFilters = {}, className }: CandidateAdvancedFilterProps) {
  const [searchKw, setSearchKw] = useState(initialFilters.search || '');
  const [minExp, setMinExp] = useState<number | null>(initialFilters.minExperience ?? null);
  const [maxExp, setMaxExp] = useState<number | null>(initialFilters.maxExperience ?? null);
  const [minSal, setMinSal] = useState<number | null>(initialFilters.minSalary ?? null);
  const [maxSal, setMaxSal] = useState<number | null>(initialFilters.maxSalary ?? null);
  const [provId, setProvId] = useState<number | null>(initialFilters.provinceId ?? null);
  const [selectedDistrictIds, setSelectedDistrictIds] = useState<number[]>(initialFilters.districtId ? [initialFilters.districtId] : []);

  const [activeDropdown, setActiveDropdown] = useState<'location' | 'salary' | 'experience' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: provinces = [] } = useProvinces(1);
  const { data: districts = [] } = useDistricts(provId || undefined);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const applyFilters = () => {
    onFilter({
      search: searchKw || undefined,
      minExperience: minExp !== null ? minExp : undefined,
      maxExperience: maxExp !== null ? maxExp : undefined,
      minSalary: minSal !== null ? minSal : undefined,
      maxSalary: maxSal !== null ? maxSal : undefined,
      provinceId: provId !== null ? provId : undefined,
      districtId: selectedDistrictIds[0] || undefined,
    });
    setActiveDropdown(null);
  };

  const resetFilters = () => {
    setSearchKw('');
    setMinExp(null);
    setMaxExp(null);
    setMinSal(null);
    setMaxSal(null);
    setProvId(null);
    setSelectedDistrictIds([]);
    onFilter({
      search: undefined,
      minExperience: undefined,
      maxExperience: undefined,
      minSalary: undefined,
      maxSalary: undefined,
      provinceId: undefined,
      districtId: undefined,
    });
    setActiveDropdown(null);
  };

  return (
    <div className={cn("relative flex flex-col md:flex-row items-stretch bg-white rounded-2xl shadow-xl border border-gray-100 p-2 gap-2 md:gap-0", className)} ref={containerRef}>
      
      {/* Keyword Search */}
      <div className="flex-[1.5] flex items-center px-3 group min-w-0">
        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 shrink-0 mr-2">
          <Search size={18} />
        </div>
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Từ khóa, kỹ năng (vd: Chef)..."
            value={searchKw}
            onChange={(e) => setSearchKw(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            className="w-full text-sm font-semibold text-gray-700 bg-transparent outline-none placeholder:text-gray-300 pr-8"
          />
          {searchKw && (
            <button
              onClick={() => { setSearchKw(''); onFilter({ search: undefined }); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 transition-colors"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>
      <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

      {/* Location Dropdown */}
      <div className="relative flex-1 group">
        <button
          onClick={() => setActiveDropdown(activeDropdown === 'location' ? null : 'location')}
          className={cn(
            "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
            "hover:bg-gray-50",
            activeDropdown === 'location' && "bg-gray-50"
          )}
        >
          <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-green-500 shrink-0">
            <MapPin size={18} />
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="text-sm font-semibold text-gray-700 truncate">
              {provId ? (selectedDistrictIds[0] ? districts.find((d: any) => d.id === selectedDistrictIds[0])?.name : provinces.find((p: any) => p.id === provId)?.name) : "Toàn quốc"}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {provId !== null && (
              <div
                onClick={(e) => { e.stopPropagation(); setProvId(null); setSelectedDistrictIds([]); onFilter({ provinceId: undefined, districtId: undefined }); }}
                className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
              >
                <X size={12} />
              </div>
            )}
            <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'location' && "rotate-180")} />
          </div>
        </button>

        {activeDropdown === 'location' && (
          <div className="absolute top-full right-0 mt-3 w-full md:w-[700px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="loc-type" defaultChecked className="accent-orange-500" />
                  <span className="text-sm font-medium text-gray-700">Tỉnh, Quận/huyện cũ</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer opacity-50">
                  <input type="radio" name="loc-type" className="accent-orange-500" />
                  <span className="text-sm font-medium text-gray-700">Tỉnh, Phường/xã mới</span>
                </label>
              </div>
              <button onClick={() => { setProvId(null); setSelectedDistrictIds([]); }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
            </div>

            <div className="flex h-[300px]">
              {/* Left: Provinces */}
              <div className="w-2/5 border-r overflow-y-auto custom-scrollbar">
                <div className="p-2 space-y-1">
                  {provinces.map((p: any) => (
                    <div
                      key={p.id}
                      onClick={() => { setProvId(p.id); setSelectedDistrictIds([]); }}
                      className={cn(
                        "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-all",
                        provId === p.id ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      <span className="truncate">{p.name}</span>
                      <ChevronDown size={14} className="-rotate-90 text-gray-300" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Districts */}
              <div className="w-3/5 overflow-y-auto custom-scrollbar p-4">
                {provId ? (
                  <div className="grid grid-cols-1 gap-1">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">QUẬN / HUYỆN</p>
                    {districts.map((d: any) => (
                      <label
                        key={d.id}
                        className={cn(
                          "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer",
                          selectedDistrictIds.includes(d.id) ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        <span>{d.name}</span>
                        <div className={cn(
                          "w-4 h-4 rounded border flex items-center justify-center",
                          selectedDistrictIds.includes(d.id) ? "bg-orange-500 border-orange-500" : "border-gray-300"
                        )}>
                          {selectedDistrictIds.includes(d.id) && <Check size={12} className="text-white" />}
                        </div>
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={selectedDistrictIds.includes(d.id)}
                          onChange={() => setSelectedDistrictIds(prev => prev.includes(d.id) ? prev.filter(i => i !== d.id) : [d.id])}
                        />
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-3">
                    <MapPin size={40} className="opacity-20" />
                    <p className="text-sm">Vui lòng chọn Tỉnh/Thành phố</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
              <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
              <button onClick={applyFilters} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
            </div>
          </div>
        )}
      </div>
      <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

      {/* Salary Dropdown */}
      <div className="relative flex-1 group">
        <button
          onClick={() => setActiveDropdown(activeDropdown === 'salary' ? null : 'salary')}
          className={cn(
            "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
            "hover:bg-gray-50",
            activeDropdown === 'salary' && "bg-gray-50"
          )}
        >
          <div className="w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center text-yellow-600 shrink-0">
            <DollarSign size={18} />
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="text-sm font-semibold text-gray-700 truncate">
              {minSal === null && maxSal === null ? "Mức lương" :
                minSal === 0 && maxSal === 0 ? "Thoả thuận" :
                  minSal !== null && maxSal !== null ? `${minSal / 1000000} - ${maxSal / 1000000}tr` :
                    minSal !== null ? `Trên ${minSal / 1000000}tr` :
                      maxSal !== null ? `Dưới ${maxSal / 1000000}tr` : "Mức lương"}
            </p>
          </div>
          <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'salary' && "rotate-180")} />
        </button>

        {activeDropdown === 'salary' && (
          <div className="absolute top-full left-0 mt-3 w-[320px] md:w-[450px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
              <h3 className="font-bold text-gray-800">Chọn khoảng lương</h3>
              <button onClick={() => { setMinSal(null); setMaxSal(null); }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
            </div>
            <div className="max-h-[350px] overflow-y-auto overflow-x-hidden custom-scrollbar p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
                {SALARY_RANGES.map((range, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setMinSal(range.min); setMaxSal(range.max); }}
                    className={cn(
                      "flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all",
                      (minSal === range.min && maxSal === range.max)
                        ? "border-orange-500 bg-orange-50/50 text-orange-600 font-semibold"
                        : "border-gray-100 hover:border-orange-200 hover:bg-gray-50 text-gray-600"
                    )}
                  >
                    <div className={cn(
                      "w-4 h-4 rounded-full border flex items-center justify-center transition-all shrink-0",
                      (minSal === range.min && maxSal === range.max) ? "border-orange-500 border-4" : "border-gray-300"
                    )} />
                    <span className="text-sm truncate">{range.label}</span>
                  </button>
                ))}
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">Hoặc nhập khoảng lương (triệu VNĐ)</p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    placeholder="Từ"
                    value={minSal ? minSal / 1000000 : ''}
                    onChange={(e) => setMinSal(e.target.value ? Number(e.target.value) * 1000000 : null)}
                    className="flex-1 h-10 px-3 rounded-xl border outline-none text-sm focus:border-orange-500 transition-colors"
                  />
                  <span className="text-gray-400 font-medium">-</span>
                  <input
                    type="number"
                    placeholder="Đến"
                    value={maxSal ? maxSal / 1000000 : ''}
                    onChange={(e) => setMaxSal(e.target.value ? Number(e.target.value) * 1000000 : null)}
                    className="flex-1 h-10 px-3 rounded-xl border outline-none text-sm focus:border-orange-500 transition-colors"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
              <button onClick={() => setActiveDropdown(null)} className="px-4 h-9 rounded-xl border bg-white text-sm font-bold text-gray-600 hover:bg-gray-50">Hủy</button>
              <button onClick={applyFilters} className="px-6 h-9 rounded-xl bg-orange-500 text-sm font-bold text-white shadow-md hover:bg-orange-600">Áp dụng</button>
            </div>
          </div>
        )}
      </div>
      <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

      {/* Experience Dropdown */}
      <div className="relative flex-1 group">
        <button
          onClick={() => setActiveDropdown(activeDropdown === 'experience' ? null : 'experience')}
          className={cn(
            "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
            "hover:bg-gray-50",
            activeDropdown === 'experience' && "bg-gray-50"
          )}
        >
          <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-400 shrink-0">
            <Briefcase size={18} />
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="text-sm font-semibold text-gray-700 truncate">
              {minExp === null && maxExp === null ? "Kinh nghiệm" :
                minExp === 0 && maxExp === 0 ? "Chưa có KN" :
                  minExp !== null && maxExp !== null ? `${minExp} - ${maxExp} năm` :
                    minExp !== null ? `Trên ${minExp} năm` :
                      maxExp !== null ? `Dưới ${maxExp} năm` : "Kinh nghiệm"}
            </p>
          </div>
          <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'experience' && "rotate-180")} />
        </button>

        {activeDropdown === 'experience' && (
          <div className="absolute top-full right-0 mt-3 w-[320px] md:w-[450px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
              <h3 className="font-bold text-gray-800">Chọn khoảng kinh nghiệm</h3>
              <button onClick={() => { setMinExp(null); setMaxExp(null); }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
            </div>
            <div className="max-h-[350px] overflow-y-auto overflow-x-hidden custom-scrollbar p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
                {EXPERIENCE_RANGES.map((range, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setMinExp(range.min); setMaxExp(range.max); }}
                    className={cn(
                      "flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all",
                      (minExp === range.min && maxExp === range.max)
                        ? "border-orange-500 bg-orange-50/50 text-orange-600 font-semibold"
                        : "border-gray-100 hover:border-orange-200 hover:bg-gray-50 text-gray-600"
                    )}
                  >
                    <div className={cn(
                      "w-4 h-4 rounded-full border flex items-center justify-center transition-all shrink-0",
                      (minExp === range.min && maxExp === range.max) ? "border-orange-500 border-4" : "border-gray-300"
                    )} />
                    <span className="text-sm truncate">{range.label}</span>
                  </button>
                ))}
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">Hoặc nhập khoảng năm kinh nghiệm</p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    placeholder="Từ (năm)"
                    value={minExp ?? ''}
                    onChange={(e) => setMinExp(e.target.value ? Number(e.target.value) : null)}
                    className="flex-1 h-10 px-3 rounded-xl border outline-none text-sm focus:border-orange-500 transition-colors"
                  />
                  <span className="text-gray-400 font-medium">-</span>
                  <input
                    type="number"
                    placeholder="Đến (năm)"
                    value={maxExp ?? ''}
                    onChange={(e) => setMaxExp(e.target.value ? Number(e.target.value) : null)}
                    className="flex-1 h-10 px-3 rounded-xl border outline-none text-sm focus:border-orange-500 transition-colors"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
              <button onClick={() => setActiveDropdown(null)} className="px-4 h-9 rounded-xl border bg-white text-sm font-bold text-gray-600 hover:bg-gray-50">Hủy</button>
              <button onClick={applyFilters} className="px-6 h-9 rounded-xl bg-orange-500 text-sm font-bold text-white shadow-md hover:bg-orange-600">Áp dụng</button>
            </div>
          </div>
        )}
      </div>

      {/* Actions Button */}
      <div className="p-1 flex gap-2">
        <button
          onClick={resetFilters}
          className="flex items-center justify-center w-12 h-12 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
          title="Xóa bộ lọc"
        >
          <X size={18} />
        </button>
        <button
          onClick={applyFilters}
          className="flex-1 md:flex-none flex items-center justify-center gap-2 h-10 md:h-full px-8 rounded-xl bg-orange-500 text-white font-bold text-sm shadow-lg hover:bg-orange-600"
        >
          <Search size={18} />
          <span className="md:hidden lg:inline">Tìm kiếm</span>
        </button>
      </div>
    </div>
  );
}
